import { useState, VFC } from "react";

import {
  Button,
  Collapse,
  FormControl,
  FormLabel,
  makeStyles,
  QuoteUrgency,
  TextInput,
} from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import i18n from "app/i18n";
import { Guide } from "components/Guide";
import Text from "components/Text";
import YesNoRadio from "components/YesNoRadio";
import { DateTime } from "luxon";
import { Controller, FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { StepBlock } from "../../_components/StepBlock";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface ShipmentDetailsStepTagProps {
  commodities?: string;
  poReferenceNumber?: string;
}

const ShipmentDetailsStepTag: React.FC<ShipmentDetailsStepTagProps> = ({ commodities, poReferenceNumber }) => {
  const parts = [];

  if (commodities) {
    const commodityList = commodities
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (commodityList.length > 0) {
      const displayCommodities = commodityList.slice(0, 2);
      const excessCount = commodityList.length - 2;

      let commodityText = displayCommodities.join(", ");
      if (excessCount > 0) {
        commodityText += ` +${excessCount} more`;
      }
      parts.push(commodityText);
    }
  }

  const poText = poReferenceNumber ? `PO# ${poReferenceNumber}` : null;

  if (parts.length === 0 && !poText) {
    return null;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      {parts.length > 0 && <span className="text-blue-600 font-bold text-sm">{parts.join("  ")}</span>}
      {poText && <span className="text-blue-600 font-normal text-sm">&nbsp;{poText}</span>}
    </div>
  );
};

interface UrgencyOption {
  id: string;
  label: string;
  value: string;
}

const getUrgencyOptions = (): UrgencyOption[] => {
  const now = DateTime.now();
  return [
    { id: "1", label: i18n.t("xHours", { ns: "shipper", count: 12 }), value: now.plus({ hour: 12 }).toISO() },
    { id: "2", label: i18n.t("xHours", { ns: "shipper", count: 24 }), value: now.plus({ day: 1 }).toISO() },
    { id: "3", label: i18n.t("xHours", { ns: "shipper", count: 48 }), value: now.plus({ day: 2 }).toISO() },
    { id: "4", label: i18n.t("xDays", { ns: "shipper", count: 5 }), value: now.plus({ day: 5 }).toISO() },
    { id: "5", label: i18n.t("xDays", { ns: "shipper", count: 7 }), value: now.plus({ day: 7 }).toISO() },
  ];
};

const useStyles = makeStyles(() => ({
  urgency: {
    padding: "9px",
  },
}));

const ShipmentDetailsStep: VFC<{ methods: UseFormReturn<FinalSchema>; onSubmit: (data: FinalSchema) => void }> = ({
  methods,
  onSubmit,
}) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [urgencyOptions] = useState(getUrgencyOptions());
  const [selectedUrgency, setSelectedUrgency] = useState<string>();
  const classes = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isHazardous = useWatch({ control: methods.control, name: "isHazardous" });

  const commodities = useWatch({ control: methods.control, name: "commodities" });
  const includeCustomsClearance = useWatch({ control: methods.control, name: "includeCustomsClearance" });
  const poReferenceNumber = useWatch({ control: methods.control, name: "poReferenceNumber" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Partners);
    setIsCollapsed(true);
  };

  if (stepIndex < QuoteStepIndex.Shipment) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 w-full">
        <StepBlock
          title={t("common:shipmentDetails")}
          headerBoxProps={{ onClick: () => setIsCollapsed(!isCollapsed) }}
          stepNumber={4}
          tag={<ShipmentDetailsStepTag commodities={commodities} poReferenceNumber={poReferenceNumber} />}
          footer={
            stepIndex <= QuoteStepIndex.Shipment ? (
              <div className="flex justify-between mt-2 w-full">
                <div />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={methods.handleSubmit(submit)}
                  style={{ width: 150 }}
                >
                  {t("common:next")}
                </Button>
              </div>
            ) : null
          }
        >
          <Collapse in={!isCollapsed}>
            <div className="grid grid-cols-4 gap-x-3 p-4 gap-y-4  mt-6">
              <div className="col-span-4">
                <Text size="medium" weight="bold" typographyProps={{ className: "text-grey-500" }}>
                  {t("shipper:shipmentDetailsStep.referenceNoUrgency")}
                </Text>
              </div>

              <div>
                <FormControl fullWidth>
                  <FormLabel>{t("shipper:PORefNumber")}</FormLabel>
                  <Controller
                    control={methods.control}
                    name="poReferenceNumber"
                    render={({ field }) => (
                      <TextInput
                        fullWidth
                        value={field.value}
                        onChange={(e) => setQuoteRequestAction({ reference_number: e.target.value })}
                      />
                    )}
                  />
                </FormControl>
              </div>

              <div>
                <FormControl fullWidth>
                  <FormLabel>{t("shipper:urgency")}</FormLabel>
                  <QuoteUrgency
                    className={classes.urgency}
                    data={urgencyOptions}
                    getLabel={(option) => {
                      const urgencyOption = urgencyOptions.find((op) => op.id === option);
                      return t("shipper:urgencyLabel", { type: "Quote", label: urgencyOption?.label ?? "" });
                    }}
                    value={selectedUrgency}
                    onChange={(_id, urgency) => {
                      setSelectedUrgency(urgency.id);
                      setQuoteRequestAction({ deadline_respond_at: urgency.value });
                    }}
                    onClear={() => {
                      setSelectedUrgency(undefined);
                      setQuoteRequestAction({ deadline_respond_at: undefined });
                    }}
                  />
                </FormControl>
              </div>

              <div className="col-span-2" />

              <div>
                <Controller
                  control={methods.control}
                  name="commodities"
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value && !fieldState.error && includeCustomsClearance}>
                      <FormStringConcat
                        margin="dense"
                        label={t("common:commodities")}
                        TextInputProps={{ required: includeCustomsClearance }}
                        value={field.value ?? ""}
                        onChange={(value) => setQuoteRequestAction({ load_spec: { commodities: value } })}
                        highlight={!!methods.formState.errors.commodities}
                      />
                    </Guide>
                  )}
                />
              </div>

              <div>
                <FormControl fullWidth margin={"dense"}>
                  <FormLabel>{t("shipper:shipmentDetailsStep.hazardousLabel")}</FormLabel>
                  <Controller
                    control={methods.control}
                    name="isHazardous"
                    render={({ field }) => (
                      <YesNoRadio
                        value={field.value}
                        onChange={(value) => setQuoteRequestAction({ is_hazardous: value })}
                        required
                      />
                    )}
                  />
                </FormControl>
              </div>

              <div>
                <FormControl fullWidth margin={"dense"}>
                  <FormLabel>{t("shipper:shipmentDetailsStep.insuranceLabel")}</FormLabel>
                  <Controller
                    control={methods.control}
                    name="includeCargoInsurance"
                    render={({ field }) => (
                      <YesNoRadio
                        value={field.value}
                        onChange={(value) => setQuoteRequestAction({ insurance_required: value })}
                        required
                      />
                    )}
                  />
                </FormControl>
              </div>

              <div>
                <FormControl fullWidth margin={"dense"}>
                  <FormLabel>{t("shipper:shipmentDetailsStep.customsLabel")}</FormLabel>
                  <Controller
                    control={methods.control}
                    name="includeCustomsClearance"
                    render={({ field }) => (
                      <YesNoRadio
                        value={field.value}
                        onChange={(value) => setQuoteRequestAction({ include_customs_clearance: value })}
                        required
                      />
                    )}
                  />
                </FormControl>
              </div>

              <div className="col-span-3">
                <Collapse in={isHazardous}>
                  <Controller
                    control={methods.control}
                    name="hazardousDetails"
                    render={({ field, fieldState }) => (
                      <Guide showGuide={!field.value && !fieldState.error && isHazardous}>
                        <TextInput
                          multiline
                          translate="no"
                          rows={3}
                          label={t("shipper:shipmentDetailsStep.hazardousDetailsLabel")}
                          placeholder={t("shipper:shipmentDetailsStep.hazardousDetailsPlaceholder")}
                          fullWidth
                          value={field.value}
                          onChange={(e) => setQuoteRequestAction({ hazardous_goods_details: e.target.value })}
                          highlight={!!methods.formState.errors.hazardousDetails}
                          required
                        />
                      </Guide>
                    )}
                  />
                </Collapse>
              </div>
              <div />

              <div className="col-span-3">
                <Controller
                  control={methods.control}
                  name="additionalNotes"
                  render={({ field }) => (
                    <TextInput
                      multiline
                      translate="no"
                      rows={3}
                      label={t("common:additionalNotes")}
                      placeholder={t("shipper:additionalNotesPlaceholder")}
                      fullWidth
                      value={field.value}
                      onChange={(e) => setQuoteRequestAction({ note: e.target.value })}
                    />
                  )}
                ></Controller>
              </div>
            </div>
          </Collapse>
        </StepBlock>
      </div>
    </FormProvider>
  );
};

export default ShipmentDetailsStep;
